/**
 * Explore timeseries level of detail — glue between the explore pages and `TimeseriesLodController`.
 *
 * Every explore chart loads the whole day at 1 Hz (the base). A brush zoom fetches a finer window (10 Hz,
 * then RAW) for the brushed stretch; each change splices the window over the base — or drops it, back to
 * 1 Hz, when the zoom is cleared — and hands the rows back to the page, which rebuilds its chart config.
 *
 * Ported from Alinghi, adapted to RaceSight's pages: the dataset page loads every chart in one fetch, so it
 * runs ONE controller for the page (not one per chart), like the fleet page.
 */
import { debug } from './console';
import { getDataByChannels, type ChannelBucketAggregate } from '../store/unifiedDataAPI';
import {
  applyExploreResponseRenames,
  capExploreResamplePlan,
  coarserApiResolution,
  exploreLodDetailGroups,
  exploreLodFinestResolution,
  mergeTimeseriesDataPointsByTimestamp,
  splitExploreLodPinnedChannels,
  type ChannelValuesApiResolution,
  type ChartLikeForResample,
  type ExploreResampleFetchPlan,
} from './timeseriesSeriesTransforms';
import { TimeseriesLodController, type LodTier, type LodWindow } from './timeseriesLodController';

/** The base grid: the whole day is loaded at this, finer data only per zoom window. */
export const EXPLORE_LOD_BASE_RESOLUTION: ChannelValuesApiResolution = '1s';

const TIER_RESOLUTION: Record<LodTier, ChannelValuesApiResolution> = { '10HZ': '100ms', RAW: null };

export interface ExploreLodHandle {
  /**
   * Any zoom change; only a return to the whole day matters (→ back to the 1 Hz base). `fullView`: the
   * chart says it is showing its whole range.
   */
  onVisibleRange: (minMs: number, maxMs: number, phase: 'live' | 'settled', fullView?: boolean) => void;
  /** Brush release: load the finer data the brushed range needs; true once it was handed to the page. */
  prepareZoom: (minMs: number, maxMs: number) => Promise<boolean>;
  dispose: () => void;
}

/**
 * The chart as explore loads it: every series asks for RAW. There is no per-series grid to pick — the day
 * loads at 1 Hz and a brush zoom brings in 10 Hz, then RAW (any `dataResample` saved on an older chart is
 * ignored). Apply it to the saved chart objects before building plans, so plan and processing agree.
 */
export function exploreLodChart<T extends { series?: unknown[] }>(chart: T): T {
  if (!Array.isArray(chart?.series)) {return chart;}
  return {
    ...chart,
    series: chart.series.map((ser) => ({ ...(ser as Record<string, unknown>), dataResample: 'RAW' })),
  };
}

/** Every chart's series as one chart: pinning and detail groups only look at the series. */
export function exploreLodPlanChart(charts: ReadonlyArray<ChartLikeForResample>): ChartLikeForResample {
  return { series: charts.flatMap((c) => c.series ?? []) } as ChartLikeForResample;
}

/**
 * Plan to fetch the base with, or null when level of detail does not apply (nothing finer than 1 Hz to
 * add — e.g. only cumulative series, which stay RAW) — then the page fetches the real plan as before.
 */
export function exploreLodBasePlan(
  chart: ChartLikeForResample,
  plan: ExploreResampleFetchPlan | null | undefined,
): ExploreResampleFetchPlan | null {
  if (!plan) {return null;}
  const split = splitExploreLodPinnedChannels(plan, chart);
  if (exploreLodDetailGroups(split, chart, EXPLORE_LOD_BASE_RESOLUTION).length === 0) {return null;}
  return capExploreResamplePlan(split, chart, EXPLORE_LOD_BASE_RESOLUTION);
}

// ----- Splicing a finer window into the base -----

/** Columns every row carries whatever its grid: never "replaced" by a window. */
const LOD_SHARED_KEYS = new Set([
  'x', 'datetime', 'timestamp', 'ts', 'race_number', 'leg_number', 'grade', 'source_id', 'source_name',
]);

/** Data columns carried by detail rows (sampled from the first rows) — the ones the window replaces. */
export function lodDetailColumns(detail: ReadonlyArray<object>): Set<string> {
  const cols = new Set<string>();
  const n = Math.min(detail.length, 50);
  for (let i = 0; i < n; i++) {
    for (const k of Object.keys(detail[i])) {
      if (!LOD_SHARED_KEYS.has(k.toLowerCase())) {cols.add(k);}
    }
  }
  return cols;
}

function lowerBoundByTime<P>(rows: readonly P[], t: number, timeOf: (p: P) => number): number {
  let lo = 0;
  let hi = rows.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (timeOf(rows[mid]) < t) {lo = mid + 1;}
    else {hi = mid;}
  }
  return lo;
}

/** `rows` when already time-sorted, else a sorted copy. */
export function rowsSortedByTime<P>(rows: P[], timeOf: (p: P) => number): P[] {
  for (let i = 1; i < rows.length; i++) {
    if (timeOf(rows[i]) < timeOf(rows[i - 1])) {
      return [...rows].sort((p, q) => timeOf(p) - timeOf(q));
    }
  }
  return rows;
}

/**
 * Lay a finer window's rows over time-sorted base rows.
 *
 * Inside [startMs, endMs] the detail rows are added and the base rows lose `replacedKeys` — so a series
 * fetched at the finer grid draws only from the detail, while a series the window did not refetch (a pinned
 * cumulative one) keeps its base samples. `replacedKeys` null drops the base rows inside the window outright
 * (fleet: one grid, every column refetched). Outside the window nothing changes, so the line runs on at the
 * base grid either side.
 */
export function spliceLodWindow<P extends object>(
  base: readonly P[],
  detail: readonly P[],
  startMs: number,
  endMs: number,
  timeOf: (p: P) => number,
  replacedKeys: ReadonlySet<string> | null,
): P[] {
  const inWindow = detail.filter((p) => {
    const t = timeOf(p);
    return t >= startMs && t <= endMs;
  });
  if (inWindow.length === 0) {return base as P[];}
  const lo = lowerBoundByTime(base, startMs, timeOf);
  const hi = lowerBoundByTime(base, endMs + 1e-6, timeOf);

  const kept: P[] = [];
  if (replacedKeys) {
    for (let i = lo; i < hi; i++) {
      const copy = { ...base[i] } as Record<string, unknown>;
      let hasData = false;
      for (const k of Object.keys(copy)) {
        if (replacedKeys.has(k)) {delete copy[k];}
        else if (!LOD_SHARED_KEYS.has(k.toLowerCase())) {hasData = true;}
      }
      if (hasData) {kept.push(copy as P);}
    }
  }

  const sortedDetail = rowsSortedByTime(inWindow, timeOf);
  const mid: P[] = [];
  let a = 0;
  let b = 0;
  while (a < kept.length || b < sortedDetail.length) {
    if (b >= sortedDetail.length || (a < kept.length && timeOf(kept[a]) <= timeOf(sortedDetail[b]))) {
      mid.push(kept[a++]);
    } else {
      mid.push(sortedDetail[b++]);
    }
  }
  return [...base.slice(0, lo), ...mid, ...base.slice(hi)];
}

function domainOf<P>(rows: readonly P[], timeOf: (p: P) => number): { minMs: number; maxMs: number } | null {
  let minMs = Number.NaN;
  let maxMs = Number.NaN;
  for (let i = 0; i < rows.length && !Number.isFinite(minMs); i++) {minMs = timeOf(rows[i]);}
  for (let i = rows.length - 1; i >= 0 && !Number.isFinite(maxMs); i--) {maxMs = timeOf(rows[i]);}
  return Number.isFinite(minMs) && Number.isFinite(maxMs) && maxMs > minMs ? { minMs, maxMs } : null;
}

// ----- Dataset explore: one controller for the page -----

export interface ExploreLodContext<P extends object> {
  /** Every chart's series (`exploreLodPlanChart`) — used for pinning and the detail groups. */
  chart: ChartLikeForResample;
  /** The real (uncapped) plan — the one the page processes rows with. */
  plan: ExploreResampleFetchPlan;
  /** Processed base points (1 Hz grid), time-sorted. */
  base: P[];
  timeOf: (p: P) => number;
  request: {
    projectId: number;
    className: string;
    datasetId: number;
    sourceName: string;
    sourceId: number;
    date: string;
    timezone: string;
  };
  /** Window rows (renamed per the plan, merged by timestamp) → points shaped like `base`. */
  toPoints: (rows: Record<string, unknown>[]) => P[];
  /** Points to draw; may return a promise that settles once the page has applied them. */
  onPoints: (points: P[]) => void | Promise<void>;
  isStale: () => boolean;
}

/** Controller for the page, or null when level of detail does not apply. */
export function createExploreLod<P extends object>(ctx: ExploreLodContext<P>): ExploreLodHandle | null {
  const { chart, plan, request, timeOf } = ctx;
  if (!exploreLodBasePlan(chart, plan) || ctx.base.length === 0) {return null;}
  const split = splitExploreLodPinnedChannels(plan, chart);
  const detailGroups = exploreLodDetailGroups(split, chart, EXPLORE_LOD_BASE_RESOLUTION);
  const finest = exploreLodFinestResolution(split, chart, EXPLORE_LOD_BASE_RESOLUTION);
  if (finest === undefined) {return null;}
  const domain = domainOf(ctx.base, timeOf);

  const fetchWindow = async (
    tier: LodTier,
    startMs: number,
    endMs: number,
    signal: AbortSignal,
  ): Promise<P[] | null> => {
    const tierRes = TIER_RESOLUTION[tier];
    const parts = await Promise.all(
      detailGroups.map(async (g) => {
        const res = await getDataByChannels(g.requestChannels, {
          projectId: request.projectId,
          className: request.className,
          datasetId: request.datasetId,
          sourceName: request.sourceName,
          sourceId: request.sourceId,
          date: request.date,
          timezone: request.timezone,
          resolution: coarserApiResolution(g.resolution, tierRes),
          startTs: startMs / 1000,
          endTs: endMs / 1000,
          signal,
        });
        const rows = (res?.data ?? []) as unknown[];
        applyExploreResponseRenames(rows, g.responseRename);
        return rows;
      }),
    );
    if (signal.aborted) {return null;}
    const merged = parts.length === 1 ? parts[0] : mergeTimeseriesDataPointsByTimestamp(parts);
    if (merged.length === 0) {return null;}
    return ctx.toPoints(merged as Record<string, unknown>[]);
  };

  const controller = new TimeseriesLodController<P[]>({
    maxTier: finest === null ? 'RAW' : '10HZ',
    domain: () => domain,
    fetchWindow,
    onChange: (windows: LodWindow<P[]>[]) => {
      if (ctx.isStale()) {return;}
      let rows = ctx.base;
      for (const w of windows) {
        if (!w.payload) {continue;}
        rows = spliceLodWindow(rows, w.payload, w.startMs, w.endMs, timeOf, lodDetailColumns(w.payload));
      }
      const shown = windows.length > 0 ? windows[windows.length - 1].tier : '1HZ';
      debug('[exploreTimeseriesLod] showing', { shown, windows: windows.length, rows: rows.length });
      return ctx.onPoints(rows);
    },
    label: 'dataset',
  });

  return {
    onVisibleRange: (minMs, maxMs, _phase, fullView) => controller.onVisibleRange(minMs, maxMs, fullView),
    prepareZoom: (minMs, maxMs) => controller.prepare(minMs, maxMs),
    dispose: () => controller.dispose(),
  };
}

// ----- Fleet (day explore): one controller for the page, one window fetch per boat -----

export interface FleetLodSource {
  source_id: number;
  source_name: string;
  dataset_id: number;
  timezone: string | null;
}

export interface FleetLodContext {
  sources: FleetLodSource[];
  /** Base rows per source_id (1 Hz), as fetched. */
  baseRowsBySource: Map<number, Record<string, unknown>[]>;
  channels: string[];
  channelBucketAggregateByName?: Record<string, ChannelBucketAggregate>;
  request: { projectId: number; className: string; date: string };
  /** Rows to draw per source; may return a promise settling once applied. */
  onRows: (rowsBySource: Map<number, Record<string, unknown>[]>) => void | Promise<void>;
  isStale: () => boolean;
}

/** Window fetches go one boat after another, two at a time. */
const FLEET_LOD_CONCURRENCY = 2;

function rowTimeMs(row: Record<string, unknown>): number {
  const t = row.timestamp;
  if (typeof t === 'number') {return t < 1e12 ? t * 1000 : t;}
  const ts = row.ts;
  if (typeof ts === 'number') {return ts < 1e12 ? ts * 1000 : ts;}
  const d = row.Datetime ?? row.datetime;
  return d == null ? Number.NaN : new Date(d as string).getTime();
}

async function forEachBounded<T>(items: readonly T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next++];
      await fn(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

/**
 * Fleet series have no per-series grid (the day loads at 1 Hz), so the full ladder applies: 10 Hz and then
 * RAW as the user zooms in. Cumulative line types stay consistent across grids because the day load sums
 * per bucket (`channelBucketAggregateByName`), and the sum of a bucket equals the sum of its raw samples.
 */
export function createFleetExploreLod(ctx: FleetLodContext): ExploreLodHandle | null {
  if (ctx.sources.length === 0) {return null;}
  const baseBySource = new Map<number, Record<string, unknown>[]>();
  let minMs = Number.POSITIVE_INFINITY;
  let maxMs = Number.NEGATIVE_INFINITY;
  for (const [sid, rows] of ctx.baseRowsBySource) {
    const sorted = rowsSortedByTime(rows, rowTimeMs);
    baseBySource.set(sid, sorted);
    const d = domainOf(sorted, rowTimeMs);
    if (d) {
      minMs = Math.min(minMs, d.minMs);
      maxMs = Math.max(maxMs, d.maxMs);
    }
  }
  if (!(maxMs > minMs)) {return null;}
  const domain = { minMs, maxMs };

  const fetchWindow = async (
    tier: LodTier,
    startMs: number,
    endMs: number,
    signal: AbortSignal,
  ): Promise<Map<number, Record<string, unknown>[]> | null> => {
    const out = new Map<number, Record<string, unknown>[]>();
    await forEachBounded(ctx.sources, FLEET_LOD_CONCURRENCY, async (source) => {
      if (signal.aborted) {return;}
      const res = await getDataByChannels(ctx.channels, {
        projectId: ctx.request.projectId,
        className: ctx.request.className,
        datasetId: source.dataset_id,
        sourceName: source.source_name,
        sourceId: source.source_id,
        date: ctx.request.date,
        timezone: source.timezone ?? 'UTC',
        channelBucketAggregateByName: ctx.channelBucketAggregateByName,
        resolution: TIER_RESOLUTION[tier],
        startTs: startMs / 1000,
        endTs: endMs / 1000,
        signal,
      });
      const rows = ((res?.data ?? []) as Record<string, unknown>[]).map((r) => ({
        ...r,
        source_id: source.source_id,
        source_name: source.source_name,
      }));
      if (rows.length > 0) {out.set(source.source_id, rows);}
    });
    if (signal.aborted || out.size === 0) {return null;}
    return out;
  };

  const controller = new TimeseriesLodController<Map<number, Record<string, unknown>[]>>({
    maxTier: 'RAW',
    domain: () => domain,
    fetchWindow,
    onChange: (windows) => {
      if (ctx.isStale()) {return;}
      const rowsBySource = new Map<number, Record<string, unknown>[]>();
      for (const [sid, base] of baseBySource) {
        let rows = base;
        for (const w of windows) {
          const detail = w.payload?.get(sid);
          if (!detail) {continue;}
          rows = spliceLodWindow(rows, detail, w.startMs, w.endMs, rowTimeMs, null);
        }
        rowsBySource.set(sid, rows);
      }
      const shown = windows.length > 0 ? windows[windows.length - 1].tier : '1HZ';
      debug('[exploreTimeseriesLod] fleet showing', { shown, windows: windows.length });
      return ctx.onRows(rowsBySource);
    },
    label: 'fleet',
  });

  return {
    onVisibleRange: (lo, hi, _phase, fullView) => controller.onVisibleRange(lo, hi, fullView),
    prepareZoom: (lo, hi) => controller.prepare(lo, hi),
    dispose: () => controller.dispose(),
  };
}
